import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import type {Control} from 'react-hook-form';
import { useController, useWatch} from 'react-hook-form';
import {FormDataContext} from 'src/app/forms/FormDataContext';
import {FormField} from 'src/app/forms/FormField';
import type {IFormDataV2, IFormFieldV2, FormFieldTypeV2} from 'src/types/FormV2';
import {useProactFormContext} from './ProactFormContext';
import {
  evaluateFieldVisibility,
  getControllingFieldNames,
} from './visibilityEvaluator';

/**
 * Field types whose components read sibling values through FormDataContext
 * (e.g. person selection filtered by a linked project field).
 */
const FIELD_TYPES_READING_FORM_DATA: FormFieldTypeV2[] = [
  'person-select-multiple',
];

/**
 * Names of the sibling fields a field's component actually reads through
 * FormDataContext, so the bridge can subscribe narrowly instead of watching
 * the whole form.
 */
function getFormDataFieldNames(field: IFormFieldV2): string[] {
  if (field.type === 'person-select-multiple') {
    return field.personOptions?.projectFieldName
      ? [field.personOptions.projectFieldName]
      : [];
  }
  return [];
}

const FormDataBridge: React.FC<{
  control: Control<IFormDataV2>;
  names: string[];
  children: React.ReactNode;
}> = ({control, names, children}) => {
  const watched = useWatch({
    control,
    name: names,
    disabled: names.length === 0,
  });

  const values: IFormDataV2 = {};
  names.forEach((watchedName, index) => {
    values[watchedName] = watched?.[index];
  });

  return (
    <FormDataContext.Provider value={values}>
      {children}
    </FormDataContext.Provider>
  );
};

export interface ProactFieldProps {
  name: string;
}

/**
 * Renders a single metadata-defined field bound to react-hook-form state.
 *
 * Subscribes only to its own value and to the values of the fields its
 * visibility rules reference, so edits elsewhere in the form do not
 * re-render it.
 */
export const ProactField: React.FC<ProactFieldProps> = ({name}) => {
  const {fieldMap, fieldsByInvariantId, setDisplayValue, methods} =
    useProactFormContext();
  const field = fieldMap[name];

  const controllingNames = useMemo(
    () => (field ? getControllingFieldNames(field, fieldsByInvariantId) : []),
    [field, fieldsByInvariantId]
  );

  const formDataFieldNames = useMemo(
    () => (field ? getFormDataFieldNames(field) : []),
    [field]
  );

  const controllingValues = useWatch({
    control: methods.control,
    name: controllingNames,
    disabled: controllingNames.length === 0,
  });

  const {field: rhfField, fieldState} = useController({
    name,
    control: methods.control,
  });

  const handleChange = useCallback(
    (fieldName: string, value: any, displayValue?: any) => {
      setDisplayValue(fieldName, displayValue ?? value);
      rhfField.onChange(value);
    },
    [rhfField, setDisplayValue]
  );

  const valuesForRules: IFormDataV2 = {};
  controllingNames.forEach((controllingName, index) => {
    valuesForRules[controllingName] = controllingValues?.[index];
  });

  const visible = field
    ? evaluateFieldVisibility(field, fieldsByInvariantId, valuesForRules)
    : true;

  const wasVisibleRef = useRef(visible);
  useEffect(() => {
    if (wasVisibleRef.current && !visible) {
      methods.clearErrors(name);
    }
    wasVisibleRef.current = visible;
  }, [visible, name, methods]);

  if (!field) {
    return null;
  }

  if (!visible) {
    return null;
  }

  const fieldElement = (
    <FormField
      field={field}
      value={rhfField.value}
      error={fieldState.error?.message}
      onChange={handleChange}
    />
  );

  if (FIELD_TYPES_READING_FORM_DATA.includes(field.type)) {
    return (
      <FormDataBridge control={methods.control} names={formDataFieldNames}>
        {fieldElement}
      </FormDataBridge>
    );
  }

  return fieldElement;
};
